import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '@/redux/hooks'
import { AppDispatch, RootState } from '@/redux/store'
import { RoleChoice } from '@/redux/types/room'
import { ChatbotType } from '@/redux/types/configuration'
import {
  fetchTeamsByRoomId,
  createNewTeam,
  updateExistingTeam,
  deleteExistingTeam,
  bulkAssignPlayersToTeamsThunk,
} from '@/redux/asyncThunks/team'
import { BulkPlayerAssignment } from '@/api/team'
import {
  fetchTeamChatbotsByRoom,
  createNewTeamChatbot,
  deleteExistingTeamChatbot,
} from '@/redux/asyncThunks/teamChatbot'
import {
  assignDummyBotToTeamThunk,
  fetchDummyBotAssignmentThunk,
  fetchDummyBotsThunk,
  unassignDummyBotThunk,
} from '@/redux/asyncThunks/dummyBot'
import { getRoomMembers } from '@/redux/asyncThunks/room'
import { toast } from 'sonner'
import { TimerMode } from './timer-configuration/types'
import { DummyBot } from '@/api/dummyBot'

interface PendingChanges {
  playerAssignments: Record<string, number | null>
  advisorAssignments: Record<string, string[]>
  dummyAssignments: Record<string, number | null>
}

const getAssignedAdvisorId = (teamChatbot: any): string | null => {
  return teamChatbot.base_template || teamChatbot.chatbot || null
}

export function useTeamManagement(
  roomId: string,
  isOpen?: boolean,
  dummyBotsRefreshKey?: number
) {
  const dispatch = useDispatch<AppDispatch>()

  const { teams } = useAppSelector((state) => state.team)
  const { teamChatbots } = useAppSelector((state) => state.teamChatbot)
  const { advisors } = useAppSelector((state) => state.advisor)
  const { currentRoom, roomMembers } = useAppSelector(
    (state: RootState) => state.room
  )

  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({
    playerAssignments: {},
    advisorAssignments: {},
    dummyAssignments: {},
  })
  const [dummyBots, setDummyBots] = useState<DummyBot[]>([])
  const [currentDummyAssignments, setCurrentDummyAssignments] = useState<
    Record<string, number | null>
  >({})

  const prevIsOpenRef = useRef<boolean | undefined>(isOpen)

  const refreshDummyBotsAndAssignments = useCallback(async () => {
    if (!roomId) return
    const bots = await dispatch(fetchDummyBotsThunk(roomId)).unwrap()
    setDummyBots(bots)
    const assignments: Record<string, number | null> = {}
    await Promise.all(
      bots.map(async (bot) => {
        const assignment = await dispatch(
          fetchDummyBotAssignmentThunk(bot.id)
        ).unwrap()
        if (assignment && assignment.team) {
          assignments[String(assignment.team)] = assignment.bot
        }
      })
    )
    setCurrentDummyAssignments(assignments)
    setPendingChanges((prev) => {
      const nextDummyAssignments: Record<string, number | null> = {}
      Object.entries(prev.dummyAssignments).forEach(([teamId, botId]) => {
        nextDummyAssignments[teamId] = botId && bots.some((b) => b.id === botId)
          ? botId
          : null
      })
      return { ...prev, dummyAssignments: nextDummyAssignments }
    })
  }, [dispatch, roomId])

  // Helper function to initialize pendingChanges from API data
  const initializePendingChanges = useCallback(() => {
    if (teams.length > 0 && teamChatbots.length >= 0) {
      const initialAdvisorAssignments: Record<string, string[]> = {}

      teams.forEach((team) => {
        initialAdvisorAssignments[team.id.toString()] = []
      })

      teamChatbots.forEach((teamChatbot) => {
        const teamId = teamChatbot.team.toString()
        const advisorId = getAssignedAdvisorId(teamChatbot)
        if (initialAdvisorAssignments[teamId]) {
          if (advisorId) {
            initialAdvisorAssignments[teamId].push(advisorId)
          }
        }
      })

      const initialPlayerAssignments: Record<string, number | null> = {}
      roomMembers?.forEach((member) => {
        if (member.role === RoleChoice.PLAYER && member.teamId) {
          const playerIdString = String(member.member_id)
          initialPlayerAssignments[playerIdString] = member.teamId
        }
      })

      setPendingChanges({
        playerAssignments: initialPlayerAssignments,
        advisorAssignments: initialAdvisorAssignments,
        dummyAssignments: currentDummyAssignments,
      })
    }
  }, [teams, teamChatbots, roomMembers, currentDummyAssignments])

  // Reset pendingChanges when modal opens or closes (state transition)
  useEffect(() => {
    if (isOpen === undefined) {
      // If isOpen is not passed, maintain original behavior: initialize when data changes
      initializePendingChanges()
      prevIsOpenRef.current = isOpen
      return
    }

    const prevIsOpen = prevIsOpenRef.current
    const isOpening = prevIsOpen === false && isOpen === true
    const isClosing = prevIsOpen === true && isOpen === false

    // Reset to API data when modal opens or closes
    if (isOpening || isClosing) {
      initializePendingChanges()
    }

    prevIsOpenRef.current = isOpen
  }, [isOpen, initializePendingChanges])

  // Reinitialize when API data changes (only if isOpen is undefined for backward compatibility)
  useEffect(() => {
    if (isOpen === undefined) {
      initializePendingChanges()
    }
  }, [teams, teamChatbots, roomMembers, isOpen, initializePendingChanges])

  const displayableAdvisors =
    currentRoom?.game_mode !== TimerMode.SINGLE_ROUND_ADVISOR_ONLY
      ? advisors?.filter(
          (advisor) =>
            advisor.chatbottype !== ChatbotType.RANDR &&
            advisor.chatbottype !== ChatbotType.ADJUDICATION &&
            advisor.chatbottype !== ChatbotType.PREP_COACH &&
            advisor.chatbottype !== ChatbotType.GROUP_MODERATOR
        ) || []
      : advisors?.filter(
          (advisor) =>
            advisor.chatbottype !== ChatbotType.ADJUDICATION &&
            advisor.chatbottype !== ChatbotType.PREP_COACH &&
            advisor.chatbottype !== ChatbotType.GROUP_MODERATOR
        ) || []

  const players =
    roomMembers?.filter((member) => member.role === RoleChoice.PLAYER) || []

  const handleAdvisorToggle = useCallback(
    (teamId: number, advisorId: string) => {
      setPendingChanges((prev) => {
        const newAdvisorAssignments = { ...prev.advisorAssignments }

        if (!newAdvisorAssignments[teamId]) {
          newAdvisorAssignments[teamId] = []
        }

        const isCurrentlyAssigned =
          newAdvisorAssignments[teamId].includes(advisorId)

        if (isCurrentlyAssigned) {
          newAdvisorAssignments[teamId] = newAdvisorAssignments[teamId].filter(
            (id) => id !== advisorId
          )
        } else {
          newAdvisorAssignments[teamId].push(advisorId)
        }

        return {
          ...prev,
          advisorAssignments: newAdvisorAssignments,
        }
      })
    },
    []
  )

  const handleDummyToggle = useCallback((teamId: number, dummyBotId: number) => {
    setPendingChanges((prev) => {
      const nextDummyAssignments = { ...prev.dummyAssignments }
      const teamKey = teamId.toString()
      const currentTeamBot = nextDummyAssignments[teamKey] ?? null
      if (currentTeamBot === dummyBotId) {
        nextDummyAssignments[teamKey] = null
        return { ...prev, dummyAssignments: nextDummyAssignments }
      }

      Object.keys(nextDummyAssignments).forEach((key) => {
        if (nextDummyAssignments[key] === dummyBotId) {
          nextDummyAssignments[key] = null
        }
      })
      nextDummyAssignments[teamKey] = dummyBotId
      return { ...prev, dummyAssignments: nextDummyAssignments }
    })
  }, [])

  const handlePlayerToggle = useCallback((teamId: number, playerId: string) => {
    setPendingChanges((prev) => {
      const currentAssignment = prev.playerAssignments[playerId]
      const newPlayerAssignments = { ...prev.playerAssignments }

      if (currentAssignment === teamId) {
        newPlayerAssignments[playerId] = null
      } else {
        newPlayerAssignments[playerId] = teamId
      }

      return {
        ...prev,
        playerAssignments: newPlayerAssignments,
      }
    })
  }, [])

  const handleDeselectAll = useCallback(() => {
    setPendingChanges((prev) => {
      // Clear all player assignments by setting all to null
      const newPlayerAssignments: Record<string, number | null> = {}
      players.forEach((player) => {
        if (player.member_id) {
          newPlayerAssignments[String(player.member_id)] = null
        }
      })

      // Clear all advisor assignments by setting all team arrays to empty
      const newAdvisorAssignments: Record<string, string[]> = {}
      teams.forEach((team) => {
        newAdvisorAssignments[team.id.toString()] = []
      })

      return {
        playerAssignments: newPlayerAssignments,
        advisorAssignments: newAdvisorAssignments,
        dummyAssignments: Object.keys(prev.dummyAssignments).reduce(
          (acc, key) => {
            acc[key] = null
            return acc
          },
          {} as Record<string, number | null>
        ),
      }
    })
  }, [players, teams])

  const isPlayerAssignedToTeam = useCallback(
    (playerId: string, teamId: number) => {
      const pendingAssignment = pendingChanges.playerAssignments[playerId]
      if (pendingAssignment !== undefined) {
        return pendingAssignment?.toString() == teamId.toString()
      }

      const member = roomMembers.find((m) => m.member_id === playerId)
      return member?.teamId?.toString() === teamId.toString()
    },
    [pendingChanges.playerAssignments, roomMembers]
  )

  const isAdvisorAssignedToTeam = useCallback(
    (advisorId: string, teamId: number) => {
      const pendingAssignments = pendingChanges.advisorAssignments[teamId]
      if (pendingAssignments !== undefined) {
        return pendingAssignments.includes(advisorId)
      }

      return teamChatbots.some(
        (teamChatbot) =>
          getAssignedAdvisorId(teamChatbot) === advisorId &&
          teamChatbot.team?.toString() === teamId.toString()
      )
    },
    [pendingChanges.advisorAssignments, teamChatbots]
  )

  const isDummyAssignedToTeam = useCallback(
    (dummyBotId: number, teamId: number) => {
      const pending = pendingChanges.dummyAssignments[teamId.toString()]
      if (pending !== undefined) {
        return pending === dummyBotId
      }
      return currentDummyAssignments[teamId.toString()] === dummyBotId
    },
    [pendingChanges.dummyAssignments, currentDummyAssignments]
  )

  const addTeam = useCallback(
    (teamName: string, description: string) => {
      const existingTeam = teams.find(
        (team) => team.name.toLowerCase() === teamName.toLowerCase()
      )

      if (existingTeam) {
        toast.error(
          `Team name "${teamName}" already exists. Please choose a different name.`
        )
        return
      }

      dispatch(
        createNewTeam({
          roomId,
          teamData: { name: teamName, description: description.trim() },
        })
      )
    },
    [dispatch, roomId, teams]
  )

  const updateTeam = useCallback(
    async (teamId: number, teamName: string, description?: string) => {
      try {
        const existingTeam = teams.find(
          (team) =>
            team.name.toLowerCase() === teamName.toLowerCase() &&
            team.id !== teamId
        )

        if (existingTeam) {
          toast.error(
            `Team name "${teamName}" already exists. Please choose a different name.`
          )
          return
        }

        await dispatch(
          updateExistingTeam({
            teamId,
            teamData: { name: teamName, description },
          })
        ).unwrap()
      } catch (error) {
        console.error('Failed to update team:', error)
        throw error
      }
    },
    [dispatch, teams]
  )

  const deleteTeam = useCallback(async (teamId: number) => {
    try {
      await dispatch(deleteExistingTeam(teamId)).unwrap()

      // Clean up pending changes for deleted team
      setPendingChanges((prev) => {
        const newAdvisorAssignments = { ...prev.advisorAssignments }
        delete newAdvisorAssignments[teamId]

        const newPlayerAssignments = { ...prev.playerAssignments }
        Object.keys(newPlayerAssignments).forEach((playerId) => {
          if (newPlayerAssignments[playerId] === teamId) {
            newPlayerAssignments[playerId] = null
          }
        })

        return {
          playerAssignments: newPlayerAssignments,
          advisorAssignments: newAdvisorAssignments,
          dummyAssignments: prev.dummyAssignments,
        }
      })
    } catch (error) {
      console.error('Failed to delete team:', error)
      throw error
    }
  }, [])

  const getCurrentAdvisorAssignments = useCallback(
    (teamId: string): string[] => {
      return teamChatbots
        .filter((tc) => tc.team.toString() === teamId)
        .map((tc) => getAssignedAdvisorId(tc))
        .filter((id): id is string => !!id)
    },
    [teamChatbots]
  )

  const calculateAdvisorChanges = useCallback(
    (teamId: string, desiredAdvisorIds: string[]) => {
      const currentAdvisorIds = getCurrentAdvisorAssignments(teamId)

      const toRemove = teamChatbots.filter(
        (tc) => {
          const assignedAdvisorId = getAssignedAdvisorId(tc)
          if (tc.team.toString() !== teamId || !assignedAdvisorId) {
            return false
          }
          return !desiredAdvisorIds.includes(assignedAdvisorId)
        }
      )

      const toAdd = desiredAdvisorIds.filter(
        (advisorId) => !currentAdvisorIds.includes(advisorId)
      )

      return {
        toRemove,
        toAdd,
        hasChanges: toRemove.length > 0 || toAdd.length > 0,
      }
    },
    [teamChatbots, getCurrentAdvisorAssignments]
  )

  const getCurrentPlayerAssignment = useCallback(
    (playerId: string): number | null => {
      const member = roomMembers.find(
        (m) => m.member_id?.toString() === playerId
      )
      return member?.teamId || null
    },
    [roomMembers]
  )

  const calculatePlayerChanges = useCallback(() => {
    const changedAssignments: BulkPlayerAssignment[] = []

    Object.entries(pendingChanges.playerAssignments).forEach(
      ([playerId, pendingTeamId]) => {
        const currentTeamId = getCurrentPlayerAssignment(playerId)

        if (currentTeamId !== pendingTeamId) {
          const player = roomMembers.find(
            (member) => member.member_id?.toString() === playerId
          )

          if (player) {
            changedAssignments.push({
              player_id: player.member_email,
              team_id: pendingTeamId,
            })
          }
        }
      }
    )

    return changedAssignments
  }, [
    pendingChanges.playerAssignments,
    getCurrentPlayerAssignment,
    roomMembers,
  ])

  const saveTeams = useCallback(async () => {
    try {
      const changedPlayerAssignments = calculatePlayerChanges()

      let bulkAssignmentPromise = Promise.resolve()
      if (changedPlayerAssignments.length > 0) {
        bulkAssignmentPromise = dispatch(
          bulkAssignPlayersToTeamsThunk({
            roomId,
            assignments: changedPlayerAssignments,
          })
        )
          .unwrap()
          .then((response) => {
            if (response.failed_assignments.length > 0) {
              console.warn(
                'Some player assignments failed:',
                response.failed_assignments
              )
              toast.error(
                `${response.failed_count} player assignments failed. Check console for details.`
              )
            }
            if (response.successful_count > 0) {
              toast.success(
                `Successfully updated ${response.successful_count} player assignments`
              )
            }
          })
      }

      const advisorAssignmentPromises = Object.entries(
        pendingChanges.advisorAssignments
      ).map(async ([teamId, desiredAdvisorIds]) => {
        const { toRemove, toAdd, hasChanges } = calculateAdvisorChanges(
          teamId,
          desiredAdvisorIds
        )
        if (!hasChanges) {
          return Promise.resolve()
        }

        const removePromises = toRemove.map((assignment) =>
          dispatch(deleteExistingTeamChatbot(assignment.id))
        )

        const createPromises = toAdd.map((advisorId) =>
          dispatch(
            createNewTeamChatbot({
              teamId: parseInt(teamId),
              assignmentData: {
                base_chatbot: advisorId,
              },
            })
          )
        )

        await Promise.all(removePromises)
        return Promise.all(createPromises)
      })

      await Promise.all([bulkAssignmentPromise, ...advisorAssignmentPromises])

      const dummyAssignmentPromises = Object.entries(
        pendingChanges.dummyAssignments
      ).map(async ([teamId, desiredBotId]) => {
        const currentBotId = currentDummyAssignments[teamId] ?? null
        if (currentBotId === desiredBotId) return
        if (currentBotId && !desiredBotId) {
          await dispatch(unassignDummyBotThunk(currentBotId)).unwrap()
          return
        }
        if (desiredBotId) {
          await dispatch(
            assignDummyBotToTeamThunk({
              botId: desiredBotId,
              teamId: parseInt(teamId),
            })
          ).unwrap()
        }
      })

      await Promise.all(dummyAssignmentPromises)

      setPendingChanges({
        playerAssignments: {},
        advisorAssignments: {},
        dummyAssignments: {},
      })

      dispatch(getRoomMembers(roomId))
      dispatch(fetchTeamsByRoomId(roomId))
      dispatch(fetchTeamChatbotsByRoom(roomId))
      await refreshDummyBotsAndAssignments()

      return true
    } catch (error) {
      console.error('Error saving teams:', error)
      return false
    }
  }, [
    pendingChanges,
    roomMembers,
    teamChatbots,
    dispatch,
    roomId,
    calculateAdvisorChanges,
    calculatePlayerChanges,
    currentDummyAssignments,
    refreshDummyBotsAndAssignments,
  ])

  const getTableColumnStyles = () => {
    const teamCount = teams.length
    if (teamCount === 0) return {}

    const maxTeamColumns = 6
    const teamColumnWidth =
      teamCount <= maxTeamColumns
        ? Math.max(120, Math.min(200, 600 / teamCount))
        : 120

    return {
      '--team-column-width': `${teamColumnWidth}px`,
      '--name-column-width': teamCount <= 2 ? '220px' : '200px',
    } as React.CSSProperties
  }

  const getTeamMemberCount = useCallback(
    (teamId: number) => {
      const playersInTeam = players.filter((player) =>
        isPlayerAssignedToTeam(player.member_id!, teamId)
      )
      const advisorsInTeam = displayableAdvisors.filter((advisor) =>
        isAdvisorAssignedToTeam(advisor.chatbotid, teamId)
      )
      return {
        players: playersInTeam.length,
        advisors: advisorsInTeam.length,
        total: playersInTeam.length + advisorsInTeam.length,
      }
    },
    [
      players,
      displayableAdvisors,
      isPlayerAssignedToTeam,
      isAdvisorAssignedToTeam,
    ]
  )

  const hasTeamMembers = useCallback(
    (teamId: number) => {
      const memberCount = getTeamMemberCount(teamId)
      return memberCount.total > 0
    },
    [getTeamMemberCount]
  )

  useEffect(() => {
    if (roomId) {
      dispatch(fetchTeamsByRoomId(roomId))
      dispatch(fetchTeamChatbotsByRoom(roomId))
      refreshDummyBotsAndAssignments()
    }
  }, [dispatch, roomId, refreshDummyBotsAndAssignments])

  useEffect(() => {
    if (!roomId || dummyBotsRefreshKey === undefined) return
    refreshDummyBotsAndAssignments()
  }, [roomId, dummyBotsRefreshKey, refreshDummyBotsAndAssignments])

  return {
    teams,
    displayableAdvisors,
    players,
    dummyBots,
    pendingChanges,
    handleAdvisorToggle,
    handlePlayerToggle,
    handleDeselectAll,
    isPlayerAssignedToTeam,
    isAdvisorAssignedToTeam,
    isDummyAssignedToTeam,
    addTeam,
    updateTeam,
    deleteTeam,
    saveTeams,
    getTableColumnStyles,
    getTeamMemberCount,
    hasTeamMembers,
    handleDummyToggle,
  }
}
